/**
 * API Client for GenAICore Nexus
 * This service handles all API calls to the backend
 */

import { toast } from "sonner";
import { logger } from "@/lib/logger";

/**
 * Untyped JSON payload returned by the gateway. Response shapes vary per
 * endpoint (and the client merges envelope fields into `data`), so callers
 * read fields directly and narrow them as needed.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- untyped JSON from the gateway; callers narrow it
export type ApiPayload = any;

/** Error thrown by ApiClient for non-2xx JSON responses. */
export interface ApiError extends Error {
  status?: number;
  responseData?: unknown;
  isConflict?: boolean;
  isDuplicateKey?: boolean;
  existingKeyId?: unknown;
  keyData?: { name: unknown; provider: unknown };
}

type JsonRecord = Record<string, unknown>;

interface RawModelEntry {
  id?: string;
  name?: string;
  model?: string;
}

// Type definitions
export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  isActive: boolean;
  apiKey?: string; // API key for authentication with LLM services
}

export interface Provider {
  name: string;
  status: 'active' | 'degraded' | 'failed' | 'unknown';
  models: string[];
  latency: number;
  errorRate: number;
}

export interface UsageStats {
  provider: string;
  requests: number;
  tokens: number;
  avgLatency: number;
  errorRate: number;
}

export interface ApiKey {
  id: string;
  name: string;
  key: string;
  createdAt: string;
  expiresAt: string;
  status: 'active' | 'expired' | 'revoked';
}

export interface LangChainTemplate {
  id: string;
  name: string;
  description: string;
  type: 'rag' | 'chain' | 'agent' | 'completion';
  complexity: 'beginner' | 'intermediate' | 'advanced';
  popularity: number;
}

export interface LangChainIntegration {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'disabled';
  lastUsed: string;
}

// API Client Class
class ApiClient {
  private token: string | null = null;
  private apiKey: string | null = null;
  private baseUrl: string = '/api';

  constructor() {
    // Initialize token from localStorage if available
    this.token = localStorage.getItem('auth_token');
    this.apiKey = localStorage.getItem('api_key');
  }

  // Authentication methods
  isAuthenticated(): boolean {
    return !!this.token;
  }

  setToken(token: string): void {
    this.token = token;
    localStorage.setItem('auth_token', token);
  }

  clearToken(): void {
    this.token = null;
    localStorage.removeItem('auth_token');
  }

  setApiKey(key: string): void {
    this.apiKey = key;
    localStorage.setItem('api_key', key);
  }

  getApiKey(): string | null {
    return this.apiKey;
  }

  // HTTP Methods
  async get(path: string, useApiKey: boolean = false, options: { signal?: AbortSignal } = {}): Promise<ApiPayload> {
    return this.request(path, 'GET', undefined, useApiKey, options);
  }

  async post(path: string, data?: unknown, useApiKey: boolean = false, options: { signal?: AbortSignal } = {}): Promise<ApiPayload> {
    return this.request(path, 'POST', data, useApiKey, options);
  }

  async put(path: string, data?: unknown, useApiKey: boolean = false, options: { signal?: AbortSignal } = {}): Promise<ApiPayload> {
    return this.request(path, 'PUT', data, useApiKey, options);
  }

  async delete(path: string, useApiKey: boolean = false, options: { signal?: AbortSignal } = {}): Promise<ApiPayload> {
    return this.request(path, 'DELETE', undefined, useApiKey, options);
  }

  // Private helper method for requests
  private async request(path: string, method: string, data?: unknown, useApiKey: boolean = false, options: { signal?: AbortSignal } = {}): Promise<ApiPayload> {
    const url = `${this.baseUrl}${path}`;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    // Always use API key for provider-keys endpoints
    if (path.startsWith('/provider-keys') || useApiKey) {
      if (!this.apiKey) {
        // Try to get the API key if we don't have it yet
        try {
          await this.getApiKeyFromUser();
        } catch (error) {
          console.error("Failed to get API key for request", error);
          throw new Error("Access denied. No API key provided.");
        }
      }
      
      if (this.apiKey) {
        headers['X-API-Key'] = this.apiKey;
      } else {
        throw new Error("Access denied. No API key provided.");
      }
    } else if (this.token) {
      // Otherwise, if we have a token, use token-based auth
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    
    const requestOptions: RequestInit = {
      method,
      headers,
      credentials: 'same-origin',
      signal: options.signal
    };
    
    if (data) {
      requestOptions.body = JSON.stringify(data);
    }
    
    try {
      const response = await fetch(url, requestOptions);
      
      // Handle non-JSON responses
      if (!response.headers.get('content-type')?.includes('application/json')) {
        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
        }
        return { success: true };
      }
      
      const responseData = (await response.json()) as JsonRecord;
      
      if (!response.ok) {
        // Create a custom error with the response data (handle both old and new error format)
        const errorMsg = ((responseData.error as { message?: unknown } | null | undefined)?.message || responseData.message || `HTTP error ${response.status}: ${responseData.error || response.statusText}`) as string;
        const error: ApiError = new Error(errorMsg);
        
        // Add status code and response data to the error object for better handling
        error.status = response.status;
        error.responseData = responseData;
        
        // Special handling for 409 conflict errors on provider keys
        if (response.status === 409) {
          error.isConflict = true;
          
          if (path.includes('/provider-keys')) {
            logger.log('Detected provider key conflict:', responseData);
            error.isDuplicateKey = true;
            
            if (responseData.existingKeyId) {
              error.existingKeyId = responseData.existingKeyId;
            }
            
            // If the body contains name and provider, add it to the error for easier handling
            const body = data as { name?: unknown; provider?: unknown } | null | undefined;
            if (body && body.name && body.provider) {
              error.keyData = {
                name: body.name,
                provider: body.provider
              };
            }
          }
        }
        
        throw error;
      }
      
      // Unwrap standardized response envelope: { success, data, pagination, meta }
      // Merges envelope fields into data so both old and new access patterns work:
      //   response.token, response.user (from data) — works
      //   response.success (from envelope) — works
      //   response.pagination (from envelope) — works
      let unwrapped: JsonRecord = responseData;
      if (responseData.success === true && responseData.data !== undefined) {
        const { data, meta, ...envelope } = responseData;
        // If data is an object, merge envelope fields into it
        // If data is an array, wrap it so we can attach pagination/success
        if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
          unwrapped = { ...envelope, ...data };
        } else {
          // Array data (paginated lists) — keep as-is with envelope fields
          unwrapped = { ...envelope, data };
        }
      }

      // Normalize response data when key structures might differ
      if (path.includes('/provider-keys') && method !== 'GET') {
        // If the response has a 'key' field but not a 'providerKey' field, add providerKey for consistency
        if (unwrapped.key && !unwrapped.providerKey) {
          unwrapped.providerKey = unwrapped.key;
        }
        // If it has a 'providerKey' field but not a 'key' field, add key for consistency
        else if (unwrapped.providerKey && !unwrapped.key) {
          unwrapped.key = unwrapped.providerKey;
        }
      }

      return unwrapped;
    } catch (error) {
      // Expected client errors (bad link, validation, 404, rate limit) are
      // handled by callers; only surface server/network failures as errors.
      const status = (error as ApiError | undefined)?.status;
      if (typeof status === 'number' && status >= 400 && status < 500) {
        logger.log(`API ${method} ${url} -> ${status}:`, (error as Error).message);
      } else {
        console.error(`API ${method} request failed for ${url}:`, error);
      }
      throw error;
    }
  }

  // Authentication specific methods
  async login(email: string, password: string): Promise<{ token: string; user: User }> {
    const data = await this.post('/auth/login', { email, password });
    this.setToken(data.token);
    return data;
  }

  async logout(): Promise<void> {
    this.clearToken();
  }

  async getCurrentUser(): Promise<User> {
    try {
      const data = await this.get('/auth/me');
      return data.user;
    } catch (error) {
      this.clearToken();
      throw error;
    }
  }

  // API key from user info
  async getApiKeyFromUser(): Promise<string | null> {
    try {
      // If we already have an API key, return it
      if (this.apiKey) {
        return this.apiKey;
      }
      
      // Try to get the API key from the user profile
      const data = await this.get('/auth/me');
      if (data.user && data.user.apiKey) {
        this.setApiKey(data.user.apiKey);
        return data.user.apiKey;
      }
      
      // If we couldn't get an API key, throw an error
      throw new Error('No API key found for user');
    } catch (error) {
      console.error("Failed to get API key from user", error);
      // Check if we're unauthorized - likely need to redirect to login
      if (error.status === 401 || error.status === 403 || error.message?.includes('401') || error.message?.includes('403')) {
        // Force logout
        this.clearToken();
        // Handle redirect to login in the component that calls this method
        throw new Error('Authentication required');
      }
      return null;
    }
  }

  // Provider methods - These need API key authentication
  async getProviders(): Promise<Provider[]> {
    try {
      // Make sure we have a valid API key first
      await this.getApiKeyFromUser();
      
      // Then make the actual request
      const data = await this.get('/llm/providers', true);
      return data.providers || [];
    } catch (error) {
      console.error("Error fetching providers:", error);
      throw error;
    }
  }

  async getModels(): Promise<ApiPayload[]> {
    const data = await this.get('/llm/models', true);
    return data.models || [];
  }

  // ============================================================================
  // MODEL REGISTRY METHODS
  // These methods access the comprehensive model registry from LiteLLM
  // ============================================================================

  /**
   * Get all supported providers from the model registry
   */
  async getRegistryProviders(): Promise<ApiPayload[]> {
    try {
      const data = await this.get('/llm/registry/providers', true);
      return data.providers || [];
    } catch (error) {
      console.error("Error fetching registry providers:", error);
      return [];
    }
  }

  /**
   * Get the complete model registry with all providers and models
   */
  async getFullRegistry(): Promise<{ stats: ApiPayload; registry: ApiPayload }> {
    try {
      const data = await this.get('/llm/registry/all', true);
      return {
        stats: data.stats || {},
        registry: data.registry || {}
      };
    } catch (error) {
      console.error("Error fetching full registry:", error);
      return { stats: {}, registry: {} };
    }
  }

  /**
   * Get models with full metadata for a specific provider from the registry
   */
  async getRegistryModelsForProvider(providerId: string): Promise<{ provider: ApiPayload; models: ApiPayload[] }> {
    try {
      const data = await this.get(`/llm/registry/providers/${providerId}/models`, true);
      return {
        provider: data.provider || null,
        models: data.models || []
      };
    } catch (error) {
      console.error(`Error fetching registry models for ${providerId}:`, error);
      return { provider: null, models: [] };
    }
  }

  /**
   * Get metadata for a specific model from the registry
   */
  async getModelMetadata(providerId: string, modelId: string): Promise<ApiPayload | null> {
    try {
      const data = await this.get(`/llm/registry/providers/${providerId}/models/${modelId}`, true);
      return data.model || null;
    } catch (error) {
      console.error(`Error fetching model metadata for ${providerId}/${modelId}:`, error);
      return null;
    }
  }

  /**
   * Search models across all providers
   */
  async searchModels(query: string, filters?: {
    capabilities?: string[];
    maxPrice?: number;
    minContext?: number;
    excludeDeprecated?: boolean;
  }): Promise<ApiPayload[]> {
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (filters?.capabilities) params.append('capabilities', filters.capabilities.join(','));
      if (filters?.maxPrice) params.append('maxPrice', filters.maxPrice.toString());
      if (filters?.minContext) params.append('minContext', filters.minContext.toString());
      if (filters?.excludeDeprecated) params.append('excludeDeprecated', 'true');

      const data = await this.get(`/llm/registry/search?${params.toString()}`, true);
      return data.models || [];
    } catch (error) {
      console.error("Error searching models:", error);
      return [];
    }
  }

  /**
   * Get models by capability (chat, vision, embedding, etc.)
   */
  async getModelsByCapability(capability: string): Promise<ApiPayload[]> {
    try {
      const data = await this.get(`/llm/registry/capabilities/${capability}`, true);
      return data.models || [];
    } catch (error) {
      console.error(`Error fetching models with capability ${capability}:`, error);
      return [];
    }
  }

  // ============================================================================
  // END MODEL REGISTRY METHODS
  // ============================================================================

  async getModelsForProvider(provider: string): Promise<string[]> {
    try {
      // Validate provider parameter
      if (!provider || provider === 'undefined' || provider === 'null') {
        console.error(`Invalid provider parameter: ${provider}`);
        return [];
      }
      
      logger.log(`Fetching models for provider: ${provider}`);
      
      const data = await this.get(`/llm/models?provider=${encodeURIComponent(provider)}`, true);
      logger.log(`Raw models data for ${provider}:`, data);
      
      if (!data || !data.models) {
        console.error(`No models returned for provider ${provider}`);
        return [];
      }
      
      // Check if models is an array of strings or objects
      if (data.models.length > 0 && typeof data.models[0] === 'object') {
        // If models are objects with id or name properties
        const modelNames = data.models
          .filter((model: RawModelEntry | null) => model && (model.id || model.name || model.model))
          .map((model: RawModelEntry) => model.id || model.name || model.model || String(model));
        logger.log(`Extracted model names for ${provider}:`, modelNames);
        return modelNames;
      }
      
      // If models are already strings, filter out any falsy values
      const filteredModels = data.models.filter((model: unknown) => !!model);
      logger.log(`Models for ${provider} after filtering:`, filteredModels);
      return filteredModels;
    } catch (error) {
      console.error(`Failed to fetch models for provider ${provider}:`, error);
      return [];
    }
  }

  async completionRequest(payload: unknown): Promise<ApiPayload> {
    return await this.post('/llm/completion', payload, true);
  }

  async streamCompletionRequest(payload: unknown, onMessage: (message: string) => void, onComplete: () => void): Promise<void> {
    try {
      // Make sure we have a valid API key first
      await this.getApiKeyFromUser();
      
      const url = `${this.baseUrl}/llm/completion/stream`;
      
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      
      if (this.apiKey) {
        headers['X-API-Key'] = this.apiKey;
      } else {
        throw new Error("Access denied. No API key provided.");
      }
      
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        if (response.headers.get('content-type')?.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.message || `HTTP error ${response.status}: ${errorData.error || response.statusText}`);
        } else {
          throw new Error(`HTTP error ${response.status}: ${response.statusText}`);
        }
      }
      
      if (!response.body) {
        throw new Error("Response body is null");
      }
      
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          onComplete();
          break;
        }
        
        const chunk = decoder.decode(value, { stream: true });
        
        try {
          // Split by lines and process each one
          const lines = chunk.split('\n').filter(line => line.trim() !== '');
          
          for (const line of lines) {
            // Check if the line is properly formatted as SSE
            if (line.startsWith('data: ')) {
              const jsonStr = line.substring(6);
              
              // Check if we've received the [DONE] marker
              if (jsonStr.trim() === '[DONE]') {
                onComplete();
                continue;
              }
              
              try {
                const data = JSON.parse(jsonStr);
                // Handle both formats:
                // - Direct: { content: "..." } (from handleStreamingRequest)
                // - OpenAI: { choices: [{ delta: { content: "..." } }] }
                const content = data.content
                  || data.choices?.[0]?.delta?.content
                  || '';
                if (content) {
                  onMessage(content);
                }
              } catch (e) {
                console.error("Error parsing JSON from stream:", e, jsonStr);
              }
            }
          }
        } catch (e) {
          console.error("Error processing stream chunk:", e);
        }
      }
    } catch (error) {
      console.error("Error in streaming completion:", error);
      throw error;
    }
  }

  async estimateTokens(text: string, model: string, provider: string): Promise<{ input: number, output: number, cost: { input: number, output: number }}> {
    const data = await this.post('/llm/estimate-tokens', { 
      text, 
      model, 
      provider 
    }, true);
    
    return {
      input: data.tokens.input || 0,
      output: data.tokens.output || 0,
      cost: {
        input: data.cost?.input || 0,
        output: data.cost?.output || 0
      }
    };
  }

  // Admin methods
  async getUsageStats(timeRange: string = 'week'): Promise<UsageStats[]> {
    const data = await this.get(`/admin/stats/usage?timeRange=${timeRange}`);
    return data.stats || [];
  }

  async getApiKeys(): Promise<ApiKey[]> {
    const data = await this.get('/admin/api-keys');
    return data.keys || [];
  }

  async getProviderKeys() {
    const data = await this.get('/admin/provider-keys');
    return data.keys || [];
  }

  async getAvailableProviders() {
    const data = await this.get('/admin/available-providers');
    return data.providers || [];
  }

  async createProviderKey(providerKey: { 
    provider: string; 
    name: string; 
    apiKey: string;
    additionalConfig?: Record<string, unknown>;
  }) {
    const data = await this.post('/admin/provider-keys', providerKey);
    return data.key;
  }

  async updateProviderKey(id: string, data: {
    name?: string;
    apiKey?: string;
    isActive?: boolean;
    additionalConfig?: Record<string, unknown>;
  }) {
    const response = await this.put(`/admin/provider-keys/${id}`, data);
    return response.key;
  }

  async deleteProviderKey(id: string) {
    logger.log(`Attempting to delete provider key ${id}`);
    try {
      const response = await this.delete(`/provider-keys/${id}`);
      logger.log(`Successfully deleted provider key ${id}`, response);
      return response;
    } catch (error) {
      console.error(`Error deleting provider key ${id}:`, error);
      
      // If we got a server error, try waiting briefly and retrying once
      if (error instanceof Error && error.message.includes('500')) {
        logger.log(`Retrying deletion of provider key ${id} after server error`);
        try {
          // Wait 1 second before retrying
          await new Promise(resolve => setTimeout(resolve, 1000));
          const retryResponse = await this.delete(`/provider-keys/${id}`);
          logger.log(`Retry succeeded for provider key ${id}`, retryResponse);
          return retryResponse;
        } catch (retryError) {
          console.error(`Retry also failed for provider key ${id}:`, retryError);
          throw retryError;
        }
      }
      
      throw error;
    }
  }

  async createApiKey(name: string): Promise<ApiKey> {
    const data = await this.post('/auth/api-keys', { name });
    return data.apiKey;
  }

  async revokeApiKey(id: string): Promise<void> {
    await this.delete(`/admin/api-keys/${id}`);
  }

  async createDemoUser(): Promise<boolean> {
    try {
      await this.post('/admin/create-demo-user');
      return true;
    } catch (error) {
      console.error("Error creating demo user:", error);
      return false;
    }
  }

  // LangChain methods
  async getLangchainTemplates(): Promise<LangChainTemplate[]> {
    try {
      // Try to fetch templates
      const data = await this.get('/langchain/templates', true);
      
      logger.log("API received template data:", data);
      
      // Handle different response formats
      if (data && data.templates && Array.isArray(data.templates)) {
        return data.templates;
      } else if (data && Array.isArray(data)) {
        return data;
      }
      
      console.warn("Unexpected template data format:", data);
      return [];
    } catch (error) {
      console.error("Error fetching LangChain templates:", error);
      // Return empty array on error
      return [];
    }
  }

  async getLangchainProviders(): Promise<ApiPayload[]> {
    try {
      // Fetch providers with their model configurations
      const data = await this.get('/langchain/providers', true);
      
      logger.log("API received provider data:", data);
      
      // Handle different response formats
      if (data && data.providers && Array.isArray(data.providers)) {
        return data.providers;
      } else if (data && Array.isArray(data)) {
        return data;
      }
      
      console.warn("Unexpected provider data format:", data);
      return [];
    } catch (error) {
      console.error("Error fetching LangChain providers:", error);
      // Return empty array on error
      return [];
    }
  }

  async getLangchainTemplate(id: string): Promise<LangChainTemplate> {
    try {
      // Try to fetch template by ID
      const data = await this.get(`/langchain/templates/${id}`, true);
      return data.template;
    } catch (error) {
      console.error("Error fetching LangChain template:", error);
      throw error;
    }
  }

  async getLangchainIntegrations(): Promise<LangChainIntegration[]> {
    try {
      const data = await this.get('/langchain/integrations', true);
      return data.integrations || [];
    } catch (error) {
      console.error("Error fetching LangChain integrations:", error);
      return [];
    }
  }

  async createLangchainTemplate(template: LangChainTemplate): Promise<LangChainTemplate> {
    try {
      const data = await this.post('/langchain/templates', { template }, true);
      
      logger.log("API received create template response:", data);
      
      // Handle different response formats
      if (data && data.template) {
        return data.template;
      } else if (data && data.id) {
        // If the response is the template itself
        return data;
      }
      
      // Return the original template as fallback
      return template;
    } catch (error) {
      console.error("Error creating LangChain template:", error);
      throw error;
    }
  }

  async updateLangchainTemplate(id: string, template: Partial<LangChainTemplate>): Promise<LangChainTemplate> {
    try {
      const data = await this.put(`/langchain/templates/${id}`, { template }, true);
      return data.template;
    } catch (error) {
      console.error(`Error updating LangChain template ${id}:`, error);
      throw error;
    }
  }

  async deleteLangchainTemplate(id: string): Promise<void> {
    try {
      logger.log(`Deleting template with ID: ${id}`);
      await this.delete(`/langchain/templates/${id}`, true);
      logger.log(`Successfully deleted template with ID: ${id}`);
    } catch (error) {
      console.error(`Error deleting LangChain template ${id}:`, error);
      throw error;
    }
  }

  async langchainCompletion(payload: {
    provider: string;
    model: string;
    messages: Array<{ role: string; content: string }>;
    options?: Record<string, unknown>;
  }): Promise<ApiPayload> {
    try {
      return await this.post('/langchain/completion', payload, true);
    } catch (error) {
      console.error("Error executing LangChain completion:", error);
      throw error;
    }
  }

  async langchainRAG(payload: {
    provider: string;
    model: string;
    query: string;
    documents: Array<{ pageContent: string; metadata?: Record<string, unknown> }>;
    options?: Record<string, unknown>;
  }): Promise<ApiPayload> {
    try {
      // Special handling for Azure OpenAI provider
      if (payload.provider.toLowerCase() === 'azure_openai') {
        logger.log("Using Azure OpenAI provider for RAG, extra validation might be needed");
        // Setup basic options if not present
        if (!payload.options) {
          payload.options = {};
        }
      }
      
      return await this.post('/langchain/rag', payload, true);
    } catch (error) {
      console.error("Error executing LangChain RAG:", error);
      
      // Enhanced error handling for Azure OpenAI errors
      if (payload.provider.toLowerCase() === 'azure_openai') {
        // Check for specific Azure errors in the error response
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          
          // If we have a detailed error, extract it
          if (errorData.message && errorData.message.includes('Azure OpenAI')) {
            console.error("Azure OpenAI specific error:", errorData.message);
            throw new Error(errorData.message);
          }
          
          // Add more specific error handling for known Azure OpenAI issues
          if (errorData.message && errorData.message.includes('instance name')) {
            console.error("Azure OpenAI endpoint not configured");
            throw new Error("Azure OpenAI API instance name (endpoint) not found. Please configure an endpoint in your API key settings.");
          }
        }
      }
      
      throw error;
    }
  }

  async langchainMultiProvider(payload: {
    providers: string[];
    model: string;
    messages: Array<{ role: string; content: string }>;
    options?: Record<string, unknown>;
  }): Promise<ApiPayload> {
    try {
      return await this.post('/langchain/multi-provider', payload, true);
    } catch (error) {
      console.error("Error executing LangChain multi-provider chain:", error);
      throw error;
    }
  }

  async langchainReasoningAgent(payload: {
    provider: string;
    model: string;
    input: string;
    systemPrompt?: string;
    options?: Record<string, unknown>;
  }): Promise<ApiPayload> {
    try {
      return await this.post('/langchain/agent/reasoning', payload, true);
    } catch (error) {
      console.error("Error executing LangChain reasoning agent:", error);
      throw error;
    }
  }

  async langchainToolAgent(payload: {
    provider: string;
    model: string;
    input: string;
    systemPrompt?: string;
    options?: Record<string, unknown>;
  }): Promise<ApiPayload> {
    try {
      return await this.post('/langchain/agent/tool', payload, true);
    } catch (error) {
      console.error("Error executing LangChain tool agent:", error);
      throw error;
    }
  }

  // Upload method for file uploads with progress tracking
  async upload(path: string, formData: FormData, onProgress?: (progress: number) => void): Promise<ApiPayload> {
    const url = `${this.baseUrl}${path}`;
    
    const headers: HeadersInit = {};
    
    // Determine which auth mechanism to use - we always use API key for uploads when available
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    } else if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    } else {
      try {
        await this.getApiKeyFromUser();
        if (this.apiKey) {
          headers['X-API-Key'] = this.apiKey;
        }
      } catch (error) {
        console.error("Failed to get API key for upload", error);
        throw new Error("Access denied. No API key provided.");
      }
    }
    
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      // Track upload progress if callback provided
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100);
            onProgress(percentComplete);
          }
        });
      }
      
      // Handle completion
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            resolve(response);
          } catch (error) {
            resolve({ success: true });
          }
        } else {
          let errorMessage = `Upload failed: ${xhr.status} ${xhr.statusText}`;
          try {
            const errorResponse = JSON.parse(xhr.responseText);
            errorMessage = errorResponse.message || errorResponse.error || errorMessage;
          } catch (e) {
            // If we can't parse the response, use the default error message
          }
          reject(new Error(errorMessage));
        }
      };
      
      // Handle network errors
      xhr.onerror = () => {
        reject(new Error('Network error occurred during upload'));
      };
      
      // Handle timeouts
      xhr.ontimeout = () => {
        reject(new Error('Upload timed out'));
      };
      
      // Set up and start the request
      xhr.open('POST', url, true);
      
      // Set headers
      for (const [key, value] of Object.entries(headers)) {
        xhr.setRequestHeader(key, value);
      }
      
      // Send the FormData
      xhr.send(formData);
    });
  }

  // RAG Studio Methods
  async createKnowledgeBase(data: {
    name: string;
    description?: string;
    embeddingModel: string;
    vectorStore: string;
    chunkSize?: number;
    chunkOverlap?: number;
    metadata?: Record<string, unknown>;
    isPublic?: boolean;
    vectorStoreConfig?: string;
  }): Promise<ApiPayload> {
    try {
      return await this.post('/rag/knowledge-bases', data, false);
    } catch (error) {
      console.error("Error creating knowledge base:", error);
      throw error;
    }
  }

  async getKnowledgeBases(): Promise<ApiPayload[]> {
    try {
      // Use token-based auth for RAG endpoints (useApiKey=false uses Bearer token)
      const response = await this.get('/rag/knowledge-bases');
      return response.knowledgeBases || [];
    } catch (error) {
      console.error("Error fetching knowledge bases:", error);
      throw error;
    }
  }

  async getKnowledgeBase(id: string): Promise<ApiPayload> {
    try {
      const response = await this.get(`/rag/knowledge-bases/${id}`, false);
      return response.knowledgeBase || null;
    } catch (error) {
      console.error(`Error fetching knowledge base ${id}:`, error);
      throw error;
    }
  }

  async updateKnowledgeBase(id: string, data: {
    name?: string;
    description?: string;
    embeddingModel?: string;
    vectorStore?: string;
    chunkSize?: number;
    chunkOverlap?: number;
    isPublic?: boolean;
    cacheEnabled?: boolean;
  }): Promise<ApiPayload> {
    try {
      return await this.put(`/rag/knowledge-bases/${id}`, data, false);
    } catch (error) {
      console.error(`Error updating knowledge base ${id}:`, error);
      throw error;
    }
  }

  async deleteKnowledgeBase(id: string): Promise<ApiPayload> {
    try {
      return await this.delete(`/rag/knowledge-bases/${id}`, false);
    } catch (error) {
      console.error(`Error deleting knowledge base ${id}:`, error);
      throw error;
    }
  }

  async uploadDocuments(knowledgeBaseId: string, files: File[], onProgress?: (progress: number) => void): Promise<ApiPayload> {
    try {
      const formData = new FormData();
      for (const file of files) {
        formData.append('files', file);
      }
      
      const xhr = new XMLHttpRequest();
      
      // Return a promise that resolves when the upload is complete
      return new Promise((resolve, reject) => {
        xhr.open('POST', `${this.baseUrl}/rag/knowledge-bases/${knowledgeBaseId}/documents`, true);
        
        // Add authorization header
        if (this.token) {
          xhr.setRequestHeader('Authorization', `Bearer ${this.token}`);
        }
        
        // Set up progress tracking
        if (onProgress) {
          xhr.upload.onprogress = (event) => {
            if (event.lengthComputable) {
              const progress = Math.round((event.loaded / event.total) * 100);
              onProgress(progress);
            }
          };
        }
        
        // Handle response
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText);
              resolve(response);
            } catch (error) {
              reject(new Error('Invalid JSON response'));
            }
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
          }
        };
        
        // Handle errors
        xhr.onerror = function() {
          reject(new Error('Network error occurred during upload'));
        };
        
        // Send the form data
        xhr.send(formData);
      });
    } catch (error) {
      console.error(`Error uploading documents to knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async getDocuments(knowledgeBaseId: string): Promise<ApiPayload[]> {
    try {
      const response = await this.get(`/rag/knowledge-bases/${knowledgeBaseId}/documents`, false);
      return response.documents || [];
    } catch (error) {
      console.error(`Error fetching documents for knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async queryKnowledgeBase(knowledgeBaseId: string, query: string, options?: {
    retrievalMode?: 'similarity' | 'mmr' | 'hybrid';
    relevanceThreshold?: number;
    maxSources?: number;
    model?: string;
    provider?: string;
  }): Promise<ApiPayload> {
    try {
      return await this.post(`/rag/knowledge-bases/${knowledgeBaseId}/query`, {
        query,
        ...options
      }, false);
    } catch (error) {
      console.error(`Error querying knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async getQueryHistory(knowledgeBaseId: string): Promise<ApiPayload[]> {
    try {
      const response = await this.get(`/rag/knowledge-bases/${knowledgeBaseId}/queries`, false);
      return response.queries || [];
    } catch (error) {
      console.error(`Error fetching query history for knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async getKnowledgeBaseApiKeys(knowledgeBaseId: string): Promise<ApiPayload[]> {
    try {
      const response = await this.get(`/rag/knowledge-bases/${knowledgeBaseId}/api-keys`, false);
      return response.apiKeys || [];
    } catch (error) {
      console.error(`Error fetching API keys for knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async createKnowledgeBaseApiKey(knowledgeBaseId: string, data: {
    name: string;
    permissions: {
      read: boolean;
      write: boolean;
    };
  }): Promise<ApiPayload> {
    try {
      return await this.post(`/rag/knowledge-bases/${knowledgeBaseId}/api-keys`, data, false);
    } catch (error) {
      console.error(`Error creating API key for knowledge base ${knowledgeBaseId}:`, error);
      throw error;
    }
  }

  async revokeKnowledgeBaseApiKey(id: string): Promise<void> {
    try {
      await this.delete(`/rag/api-keys/${id}`, false);
    } catch (error) {
      console.error(`Error revoking API key ${id}:`, error);
      throw error;
    }
  }

  async getEmbeddingModels(): Promise<string[]> {
    try {
      // Always use token-based auth for RAG endpoints
      const response = await this.get('/rag/embedding-models', false);
      return response.models || [];
    } catch (error) {
      console.error("Error fetching embedding models:", error);
      throw error;
    }
  }
}

// Create a singleton instance
export const api = new ApiClient(); 